import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { canViewHref } from "@/lib/acl";
import { useCurrentUser, userIsRealAppAdmin } from "@/lib/auth";
import { toast } from "@/lib/toast";
import { TOURS } from "@/features/tours/catalog";
import { selectToursForUser } from "@/features/tours/selectTours";
import type { AvailableTour, ResolvedTourStep } from "@/features/tours/types";

interface ActiveTour {
  tourId: string;
  title: string;
  steps: ResolvedTourStep[];
  index: number;
}

interface TourContextValue {
  tours: AvailableTour[];
  loading: boolean;
  active: ActiveTour | null;
  start: (tourId: string) => void;
  stop: () => void;
  next: () => void;
  prev: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const [tours, setTours] = useState<AvailableTour[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<ActiveTour | null>(null);
  const finishedTitle = useRef<string | null>(null);

  const groupsKey = (user?.groups || []).join("|");

  useEffect(() => {
    if (!user || !userIsRealAppAdmin(user)) {
      setTours([]);
      setActive(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void selectToursForUser(TOURS, user, (href) => canViewHref(href, user.groups || []))
      .then((next) => {
        if (!cancelled) setTours(next);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, groupsKey]);

  const start = useCallback(
    (tourId: string) => {
      const tour = tours.find((item) => item.id === tourId);
      if (!tour || tour.steps.length === 0) return;
      setActive({ tourId: tour.id, title: tour.title, steps: tour.steps, index: 0 });
    },
    [tours],
  );

  const stop = useCallback(() => {
    finishedTitle.current = null;
    setActive(null);
  }, []);

  const next = useCallback(() => {
    setActive((current) => {
      if (!current) return null;
      const following = current.index + 1;
      if (following >= current.steps.length) {
        finishedTitle.current = current.title;
        return null;
      }
      return { ...current, index: following };
    });
  }, []);

  useEffect(() => {
    if (active || !finishedTitle.current) return;
    const title = finishedTitle.current;
    finishedTitle.current = null;
    toast.success(`„${title}“ ist durch. Gespeichert wird nur, was du selbst angeklickt hast.`);
  }, [active]);

  const prev = useCallback(() => {
    setActive((current) => {
      if (!current || current.index === 0) return current;
      return { ...current, index: current.index - 1 };
    });
  }, []);

  const value = useMemo(
    () => ({ tours, loading, active, start, stop, next, prev }),
    [tours, loading, active, start, stop, next, prev],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error("useTour muss innerhalb von TourProvider stehen.");
  }
  return ctx;
}
